import Phaser from "phaser";
import {
  BACKGROUND_PRESETS,
  DEFAULT_BACKGROUND_ID,
  DEFAULT_DEBUG_MODE,
  NATIVE_HEIGHT,
  NATIVE_WIDTH,
  PALETTE,
  UGC_CYAN,
} from "../config";
import type { BackgroundPreset, Variant } from "../config";
import type { SessionConfig } from "../types";
import type { WalletSnapshot } from "../services/Wallet";
import { InputService } from "../services/InputService";
import { TelemetryService } from "../services/TelemetryService";
import { LanguageService, t } from "../i18n";
import type { Language } from "../i18n";

// Module-level guard: the boot-time i18n.language.changed event is logged
// exactly once per page load, even if the Launcher scene restarts later
// (which happens on every language toggle).
let bootLanguageReported = false;

/**
 * View 1 — Launcher / Variant Select (ui-sketches §1).
 *
 * Operator-facing: set participant ID + variant + (Variant B only) a curated
 * Background-Upload preset, then Start. Variant defaults to A so a mis-click
 * can't leak UGC into a control run. Trip-length picker is stubbed at the
 * PRD §5 default (10 min) and not interactive in v0.3.
 *
 * Background Upload UGC slice (PRD §6 / TODO §5):
 *   - Appears only when Variant B is active.
 *   - Up/Down (or W/S) cycles through BACKGROUND_PRESETS.
 *   - Preset id is passed in SessionConfig.backgroundId; Parallax reads it
 *     and applies the override + stamps a cyan corner-mark for non-default
 *     selections.
 *   - Telemetry emits a Layer-1 "ugc.background.preview" event whenever
 *     the participant cycles to a different preset (open/close/place per
 *     PRD §10 maps to "preview" / "apply" / "dismiss" for this feature).
 */
export class LauncherScene extends Phaser.Scene {
  private controls!: InputService;
  private telemetry!: TelemetryService;
  private selectedVariant: Variant = "B";
  private participantId = "P-0001";
  private backgroundIndex = 0;
  private debugMode: boolean = DEFAULT_DEBUG_MODE;

  private variantA!: Phaser.GameObjects.Rectangle;
  private variantALabel!: Phaser.GameObjects.Text;
  private variantB!: Phaser.GameObjects.Rectangle;
  private variantBLabel!: Phaser.GameObjects.Text;
  private startButton!: Phaser.GameObjects.Rectangle;

  // Background Upload picker (Variant B only).
  private bgGroup!: Phaser.GameObjects.Container;
  private bgSwatches: Phaser.GameObjects.Rectangle[] = [];
  private bgLabel!: Phaser.GameObjects.Text;
  private bgHint!: Phaser.GameObjects.Text;
  private bgUgcMark!: Phaser.GameObjects.Image;

  // Edge-trigger guard for vertical stick cycling.
  private prevMoveY = 0;

  // Debug-mode checkbox (operator UI; default driven by VITE_DEBUG_MODE env).
  private debugCheckbox!: Phaser.GameObjects.Rectangle;
  private debugCheckmark!: Phaser.GameObjects.Text;
  private debugLabel!: Phaser.GameObjects.Text;

  // MARKET tile — Variant B only entry into ShopScene.
  private marketTile!: Phaser.GameObjects.Container;

  // Language toggle (top-right) — DE / EN chips.
  private langChipDe!: Phaser.GameObjects.Rectangle;
  private langChipDeLabel!: Phaser.GameObjects.Text;
  private langChipEn!: Phaser.GameObjects.Rectangle;
  private langChipEnLabel!: Phaser.GameObjects.Text;

  private langUnsubscribe?: () => void;

  constructor() {
    super("launcher");
  }

  create(): void {
    this.controls = new InputService(this);
    this.telemetry = new TelemetryService(this.participantId);
    this.telemetry.emit(1, "scene.enter", { scene: "launcher" });
    if (!bootLanguageReported) {
      bootLanguageReported = true;
      LanguageService.onBoot((ev) => {
        this.telemetry.emit(1, "i18n.language.changed", {
          from: ev.from ?? "",
          to: ev.to,
          source: ev.source,
        });
      });
    }
    this.cameras.main.setBackgroundColor(PALETTE.hudPanel);

    const cx = NATIVE_WIDTH / 2;

    // Participant-facing GAME title: "Knight Errand" (confirmed by
    // Jonathan 2026-05-17). The hero is still named "Roamling" inside
    // the world; "Roamling" is also the project codename (PRD §1).
    this.add
      .text(cx, 18, "K N I G H T  E R R A N D", {
        fontFamily: "monospace",
        fontSize: "12px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);
    this.add
      .text(cx, 30, t("launcher.subtitle"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    this.buildLanguageToggle();

    this.add
      .text(40, 54, t("launcher.participant"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.add
      .text(120, 54, this.participantId, {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0, 0.5);

    this.add
      .text(40, 78, t("launcher.variant"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);

    this.variantA = this.add
      .rectangle(120, 78, 88, 26, PALETTE.ground)
      .setOrigin(0, 0.5)
      .setStrokeStyle(2, PALETTE.heroOutline);
    this.variantALabel = this.add
      .text(164, 78, t("launcher.variant.a"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.variantA, () => this.setVariant("A"));

    this.variantB = this.add
      .rectangle(216, 78, 88, 26, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.variantBLabel = this.add
      .text(260, 78, t("launcher.variant.b"), {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.variantB, () => this.setVariant("B"));

    this.buildBackgroundPicker();
    this.buildMarketTile();
    this.buildDebugCheckbox();

    this.startButton = this.add
      .rectangle(cx, NATIVE_HEIGHT - 38, 110, 22, PALETTE.geo)
      .setOrigin(0.5, 0.5)
      .setStrokeStyle(1, PALETTE.heroOutline);
    this.add
      .text(cx, NATIVE_HEIGHT - 38, t("launcher.start"), {
        fontFamily: "monospace",
        fontSize: "11px",
        color: this.toHex(0x000000),
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.startButton, () => this.startSession());

    this.add
      .text(cx, NATIVE_HEIGHT - 14, t("launcher.hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);

    this.paintVariant();
    this.paintBackground();

    // Re-render the entire launcher on language change. Cheaper than rebinding
    // every text object; scene is short-lived. `shutdown` unsubscribes.
    this.langUnsubscribe = LanguageService.onChange(() => this.scene.restart());
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.langUnsubscribe?.();
      this.langUnsubscribe = undefined;
    });
  }

  /**
   * Top-right DE / EN chip pair. Active language renders highlighted (geo gold
   * border), inactive renders dim. Click flips LanguageService, which fires
   * the change event and triggers a scene restart so every other label picks
   * up the new strings.
   */
  private buildLanguageToggle(): void {
    const baseX = NATIVE_WIDTH - 56;
    const y = 12;
    const chipW = 24;
    const chipH = 14;

    this.langChipDe = this.add
      .rectangle(baseX, y, chipW, chipH, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.langChipDeLabel = this.add
      .text(baseX + chipW / 2, y, "DE", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.langChipDe, () => this.setLanguage("de"));

    this.langChipEn = this.add
      .rectangle(baseX + chipW + 2, y, chipW, chipH, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.langChipEnLabel = this.add
      .text(baseX + chipW + 2 + chipW / 2, y, "EN", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.hudDim),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.makeClickable(this.langChipEn, () => this.setLanguage("en"));

    this.paintLanguageChips();
  }

  private setLanguage(lang: Language): void {
    if (lang === LanguageService.getLanguage()) return;
    const previous = LanguageService.getLanguage();
    LanguageService.setLanguage(lang, "launcher");
    this.telemetry.emit(1, "i18n.language.changed", {
      from: previous,
      to: lang,
      source: "launcher",
    });
  }

  private paintLanguageChips(): void {
    const lang = LanguageService.getLanguage();
    const de = lang === "de";
    this.langChipDe.fillColor = de ? PALETTE.ground : PALETTE.hudPanel;
    this.langChipDe.setStrokeStyle(de ? 2 : 1, de ? PALETTE.geo : PALETTE.hudDim);
    this.langChipDeLabel.setColor(this.toHex(de ? PALETTE.hudText : PALETTE.hudDim));

    this.langChipEn.fillColor = !de ? PALETTE.ground : PALETTE.hudPanel;
    this.langChipEn.setStrokeStyle(!de ? 2 : 1, !de ? PALETTE.geo : PALETTE.hudDim);
    this.langChipEnLabel.setColor(this.toHex(!de ? PALETTE.hudText : PALETTE.hudDim));
  }

  override update(): void {
    const s = this.controls.read();
    if (s.moveX < -0.5) this.setVariant("A");
    if (s.moveX > 0.5) this.setVariant("B");

    // Edge-detect vertical for background cycling so a held stick doesn't burn presets.
    if (this.selectedVariant === "B") {
      if (s.moveY < -0.5 && this.prevMoveY >= -0.5) this.cycleBackground(-1);
      if (s.moveY > 0.5 && this.prevMoveY <= 0.5) this.cycleBackground(+1);
    }
    this.prevMoveY = s.moveY;

    if (s.jumpPressed || s.interactPressed || s.pausePressed) {
      this.startSession();
    }
  }

  private setVariant(v: Variant): void {
    if (v === this.selectedVariant) return;
    this.selectedVariant = v;
    this.telemetry.emit(1, "ui.variant_select", { variant: v });
    this.paintVariant();
    this.paintBackground();
  }

  private cycleBackground(delta: number): void {
    const n = BACKGROUND_PRESETS.length;
    this.backgroundIndex = (this.backgroundIndex + delta + n) % n;
    this.telemetry.emit(1, "ugc.background.preview", {
      preset: this.selectedPreset().id,
    });
    this.paintBackground();
  }

  private selectedPreset(): BackgroundPreset {
    return BACKGROUND_PRESETS[this.backgroundIndex]!;
  }

  private paintVariant(): void {
    const aSelected = this.selectedVariant === "A";
    this.variantA.fillColor = aSelected ? PALETTE.ground : PALETTE.hudPanel;
    this.variantA.setStrokeStyle(aSelected ? 2 : 1, aSelected ? PALETTE.heroOutline : PALETTE.hudDim);
    this.variantALabel.setColor(this.toHex(aSelected ? PALETTE.hudText : PALETTE.hudDim));

    this.variantB.fillColor = !aSelected ? PALETTE.ground : PALETTE.hudPanel;
    this.variantB.setStrokeStyle(!aSelected ? 2 : 1, !aSelected ? PALETTE.heroOutline : PALETTE.hudDim);
    this.variantBLabel.setColor(this.toHex(!aSelected ? PALETTE.hudText : PALETTE.hudDim));

    this.bgGroup.setVisible(this.selectedVariant === "B");
    if (this.marketTile) this.marketTile.setVisible(this.selectedVariant === "B");
  }

  /**
   * Variant-B-only MARKET tile that opens the shop overlay between sessions.
   * Wallet trades happen on a persistent launcher snapshot; carry-over into
   * the runner is a separate TODO (see design doc §3 / SessionConfig).
   */
  private buildMarketTile(): void {
    const x = NATIVE_WIDTH - 96;
    const y = NATIVE_HEIGHT - 78;
    this.marketTile = this.add.container(x, y);
    const bg = this.add
      .rectangle(0, 0, 80, 32, PALETTE.shopWall, 1)
      .setOrigin(0, 0)
      .setStrokeStyle(2, UGC_CYAN);
    const banner = this.add
      .text(40, 8, t("launcher.market.title"), {
        fontFamily: "monospace",
        fontSize: "10px",
        color: this.toHex(PALETTE.parchment),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    const sub = this.add
      .text(40, 22, t("launcher.market.sub"), {
        fontFamily: "monospace",
        fontSize: "6px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0.5, 0.5);
    const corner = this.add.rectangle(74, 2, 4, 4, UGC_CYAN, 1).setOrigin(0, 0);
    this.marketTile.add([bg, banner, sub, corner]);
    bg.setInteractive({ useHandCursor: true });
    bg.on("pointerdown", () => {
      this.telemetry.emit(1, "ugc.shop.button.click", { source: "launcher_tile" });
      this.openShop();
    });
    this.marketTile.setVisible(this.selectedVariant === "B");
  }

  private openShop(): void {
    // Stash telemetry into the registry so ShopScene can find it without
    // needing a back-reference to LauncherScene.
    this.registry.set("telemetryService", this.telemetry);
    this.scene.start("ShopScene", { returnTo: "launcher", walletSource: "launcher" });
  }

  private buildBackgroundPicker(): void {
    this.bgGroup = this.add.container(0, 0);

    const labelHeader = this.add
      .text(40, 112, t("launcher.background"), {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.bgGroup.add(labelHeader);

    const ugcChip = this.add
      .text(116, 112, t("launcher.background.ugc_chip"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(UGC_CYAN),
      })
      .setOrigin(0, 0.5);
    this.bgGroup.add(ugcChip);

    // Swatches: one rect per preset, laid out left-to-right.
    const swatchY = 132;
    const swatchSize = 28;
    const startX = 40;
    BACKGROUND_PRESETS.forEach((p, i) => {
      const r = this.add
        .rectangle(startX + i * (swatchSize + 8), swatchY, swatchSize, swatchSize, p.skyFar)
        .setOrigin(0, 0)
        .setStrokeStyle(1, PALETTE.hudDim);
      this.bgSwatches.push(r);
      this.bgGroup.add(r);
      this.makeClickable(r, () => {
        if (this.selectedVariant !== "B") return; // Picker is hidden anyway, defensive.
        if (this.backgroundIndex === i) return;
        this.backgroundIndex = i;
        this.telemetry.emit(1, "ugc.background.preview", {
          preset: this.selectedPreset().id,
          source: "click",
        });
        this.paintBackground();
      });

      // Mid-band stripe so the swatch reads as a parallax silhouette, not a flat square.
      const stripe = this.add
        .rectangle(
          startX + i * (swatchSize + 8),
          swatchY + swatchSize - 10,
          swatchSize,
          10,
          p.ruinsMid,
        )
        .setOrigin(0, 0);
      this.bgGroup.add(stripe);
    });

    this.bgLabel = this.add
      .text(40, 168, "", {
        fontFamily: "monospace",
        fontSize: "8px",
        color: this.toHex(PALETTE.hudText),
      })
      .setOrigin(0, 0.5);
    this.bgGroup.add(this.bgLabel);

    this.bgHint = this.add
      .text(40, 180, t("launcher.background.hint"), {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.bgGroup.add(this.bgHint);

    // Cyan UGC corner-mark preview that lights up for non-default presets.
    this.bgUgcMark = this.add.image(220, 112, "ugc.corner").setOrigin(0, 0.5);
    this.bgGroup.add(this.bgUgcMark);
  }

  /**
   * Small "Debug mode" checkbox in the bottom-left, lit when the
   * developer shortcuts (1-9 depth jump, fast-forward) are armed.
   * Defaults to `DEFAULT_DEBUG_MODE` (read from `VITE_DEBUG_MODE` at
   * build time) so participants always see an unchecked box on a
   * clean checkout, while the developer sees it pre-checked locally.
   */
  private buildDebugCheckbox(): void {
    const y = NATIVE_HEIGHT - 64;
    this.debugCheckbox = this.add
      .rectangle(20, y, 10, 10, PALETTE.hudPanel)
      .setOrigin(0, 0.5)
      .setStrokeStyle(1, PALETTE.hudDim);
    this.debugCheckmark = this.add
      .text(25, y, "x", {
        fontFamily: "monospace",
        fontSize: "9px",
        color: this.toHex(PALETTE.geo),
        fontStyle: "bold",
      })
      .setOrigin(0.5, 0.5);
    this.debugLabel = this.add
      .text(36, y, "Debug mode (1-9 depth · → fast-forward)", {
        fontFamily: "monospace",
        fontSize: "7px",
        color: this.toHex(PALETTE.hudDim),
      })
      .setOrigin(0, 0.5);
    this.makeClickable(this.debugCheckbox, () => this.toggleDebug());
    // Clicking the label is a natural affordance too; reuse the same handler.
    this.debugLabel.setInteractive({ useHandCursor: true });
    this.debugLabel.on("pointerdown", () => this.toggleDebug());
    this.paintDebugCheckbox();
  }

  private toggleDebug(): void {
    this.debugMode = !this.debugMode;
    this.telemetry.emit(1, "ui.debug_toggle", { enabled: this.debugMode });
    this.paintDebugCheckbox();
  }

  private paintDebugCheckbox(): void {
    this.debugCheckmark.setVisible(this.debugMode);
    this.debugLabel.setColor(
      this.toHex(this.debugMode ? PALETTE.hudText : PALETTE.hudDim),
    );
    this.debugCheckbox.setStrokeStyle(
      this.debugMode ? 2 : 1,
      this.debugMode ? PALETTE.geo : PALETTE.hudDim,
    );
  }

  private paintBackground(): void {
    if (!this.bgGroup) return;
    this.bgSwatches.forEach((s, i) => {
      const isSelected = i === this.backgroundIndex;
      s.setStrokeStyle(isSelected ? 2 : 1, isSelected ? UGC_CYAN : PALETTE.hudDim);
    });
    const preset = this.selectedPreset();
    const suffix = preset.id === DEFAULT_BACKGROUND_ID
      ? t("launcher.background.no_ugc")
      : t("launcher.background.ugc_active");
    // Only the generic "Default" preset gets localised; the other presets
    // are flavor place-names (City of Rain, Verdant Halls, Ember Reach)
    // treated like proper nouns and rendered as-is.
    const label = preset.id === DEFAULT_BACKGROUND_ID ? t("preset.default") : preset.label;
    this.bgLabel.setText(`${label}${suffix}`);
    this.bgUgcMark.setVisible(preset.id !== DEFAULT_BACKGROUND_ID);
  }

  private startSession(): void {
    const cfg: SessionConfig = {
      variant: this.selectedVariant,
      participantId: this.participantId,
      startedAt: performance.now(),
      backgroundId:
        this.selectedVariant === "B"
          ? this.selectedPreset().id
          : DEFAULT_BACKGROUND_ID,
      debugMode: this.debugMode,
      language: LanguageService.getLanguage(),
      // Variant-B-only: forward any Launcher-side Market trades into the
      // runner. The snapshot lives in the registry across MARKET visits;
      // we copy it (not pass by reference) so the runner can mutate its
      // own copy without affecting future Launcher visits.
      startingWallet: this.snapshotForStart(),
    };
    this.telemetry.emit(1, "ui.start_pressed", {
      variant: cfg.variant,
      backgroundId: cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
      debug: cfg.debugMode === true,
      language: cfg.language ?? "de",
    });
    if (this.selectedVariant === "B") {
      this.telemetry.emit(2, "ugc.background.apply", {
        preset: cfg.backgroundId ?? DEFAULT_BACKGROUND_ID,
      });
    }
    this.telemetry.flush();
    this.scene.start("tutorial", cfg);
  }

  /** Build the runner's starting wallet from the Launcher-side Market snapshot.
   *  Returns undefined when there's been no trading (so the runner takes its
   *  default fresh-session values). */
  private snapshotForStart(): SessionConfig["startingWallet"] {
    if (this.selectedVariant !== "B") return undefined;
    const snap = this.registry.get("launcherWalletSnapshot") as WalletSnapshot | undefined;
    if (!snap) return undefined;
    return {
      geo: snap.geo,
      hp: snap.hp,
      featherCharges: snap.featherCharges,
      inventory: snap.inventory.map((i) => ({ kind: i.kind, pickedUpAtMs: i.pickedUpAtMs })),
      doubleJump: snap.skills.doubleJump,
      escapeFeather: snap.skills.escapeFeather,
    };
  }

  private toHex(n: number): string {
    return `#${n.toString(16).padStart(6, "0")}`;
  }

  /** Wire pointerdown on a UI rect with hand-cursor feedback. */
  private makeClickable(rect: Phaser.GameObjects.Rectangle, onClick: () => void): void {
    rect.setInteractive({ useHandCursor: true });
    rect.on("pointerdown", () => {
      this.telemetry.emit(1, "ui.click", { x: Math.round(rect.x), y: Math.round(rect.y) });
      onClick();
    });
  }
}
